import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Avatar,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import { useLanguage } from '../../i18n/LanguageContext';
import { Notification } from '../../types';
import { API_URL } from '../../utils';
import { useWebSocket } from '../../hooks/useWebSocket';

type ViewType = 'calendar' | 'summary' | 'exercises' | 'bodyweight' | 'routines' | 'friends' | 'profile';

interface NotificationBadgeProps {
  fetchWithAuth: (url: string, options?: any) => Promise<Response>;
  setView: (view: ViewType, tab?: number) => void;
  onRefresh?: (refreshFunction: () => Promise<void>) => void;
  authToken: string | null;
}

// Helper functions
const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'friend_request_sent':
    case 'friend_request_accepted':
      return <PersonAddIcon />;
    case 'routine_share_sent':
    case 'routine_share_accepted':
      return <FitnessCenterIcon />;
    case 'workout_share_sent':
    case 'workout_share_accepted':
      return <FitnessCenterIcon />;
    default:
      return <NotificationsIcon />;
  }
};

const getNotificationIconColor = (type: string) => {
  switch (type) {
    case 'friend_request_sent':
    case 'friend_request_accepted':
      return '#1976d2'; // Primary blue
    case 'routine_share_sent':
    case 'routine_share_accepted':
      return '#9c27b0'; // Secondary purple
    case 'workout_share_sent':
    case 'workout_share_accepted':
      return '#ff9800'; // Orange
    default:
      return '#757575'; // Grey
  }
};

const formatRelativeTime = (dateString: string): string => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString();
  }
};

export const NotificationBadge: React.FC<NotificationBadgeProps> = ({
  fetchWithAuth,
  setView,
  onRefresh,
  authToken
}) => {

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useLanguage();

  const hasInitialLoad = useRef(false);

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetchWithAuth(`${API_URL}/notifications?limit=20`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }
      
      const data: Notification[] = await response.json();
      setNotifications(data);
      setUnreadCount(data.filter(n => !n.is_read).length);
      hasInitialLoad.current = true;
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError(t('notifications.error'));
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, t]);

  // WebSocket event handlers
  const handleNewNotification = useCallback((notification: Notification) => {
    setNotifications(prev => [notification, ...prev.slice(0, 19)]); // Keep max 20 notifications
    setUnreadCount(prev => prev + 1);
  }, []);

  const handleNotificationCountUpdate = useCallback((counts: { unread: number; total: number }) => {
    setUnreadCount(counts.unread);
  }, []);

  const handleWebSocketConnect = useCallback(() => {
    // Load notifications when WebSocket connects (if not already loaded)
    if (!hasInitialLoad.current) {
      loadNotifications();
    }
  }, [loadNotifications]);

  const handleWebSocketDisconnect = useCallback(() => {
    // setIsWebSocketConnected(false); // No longer needed
  }, []);

  // Initialize WebSocket connection
  const { acknowledgeNotification } = useWebSocket({
    token: authToken,
    onNotification: handleNewNotification,
    onNotificationCountUpdate: handleNotificationCountUpdate,
    onConnect: handleWebSocketConnect,
    onDisconnect: handleWebSocketDisconnect
  });

  // Initial load when component mounts (fallback if WebSocket is not ready)
  useEffect(() => {
    if (!hasInitialLoad.current) {
      loadNotifications();
    }
  }, [loadNotifications]);

  // Expose refresh function to parent
  useEffect(() => {
    if (onRefresh && typeof onRefresh === 'function') {
      onRefresh(loadNotifications);
    }
  }, [onRefresh, loadNotifications]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification: Notification) => {
    try {
      // Mark notification as read if it's unread
      if (!notification.is_read) {
        const response = await fetchWithAuth(`${API_URL}/notifications/mark-read`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ notificationId: notification.id })
        });
        if (!response.ok) {
          throw new Error('Failed to mark notification as read');
        }

        // Acknowledge notification via WebSocket
        acknowledgeNotification(notification.id);
        
        // Update local state immediately
        setNotifications(prev => 
          prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      // Navigate based on notification type
      if (notification.type === 'friend_request_sent') {
        setView('friends', 1); // Friend Requests tab
      } else if (notification.type === 'friend_request_accepted') {
        setView('friends', 0); // Friends tab
      } else if (notification.type === 'routine_share_sent' || notification.type === 'routine_share_accepted') {
        setView('routines');
      } else if (notification.type === 'workout_share_sent' || notification.type === 'workout_share_accepted') {
        setView('friends', 3); // Shared Workouts tab
      }

      handleClose();
    } catch (err) {
      console.error('Error handling notification click:', err);
    }
  };

  const handleClearAllNotifications = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(`${API_URL}/notifications/delete-all`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error('Failed to clear notifications');
      }
      
      // Update state immediately for better UX
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      console.error('Error clearing notifications:', err);
      setError('Failed to clear notifications');
    } finally {
      setLoading(false);
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-label={t('notifications.title')}
      >
        <Badge 
          badgeContent={unreadCount} 
          color="error"
          invisible={unreadCount === 0}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: { width: 360, maxHeight: 500 }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" component="div">
              {t('notifications.title')}
            </Typography>
          </Box>
        </Box>
        
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {error && (
          <MenuItem disabled>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </MenuItem>
        )}

        {!loading && !error && notifications.length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2" color="textSecondary">
              {t('notifications.noNotifications')}
            </Typography>
          </MenuItem>
        )}

        {!loading && !error && notifications.length > 0 && (
          <>
            {notifications.map((notification, index) => (
              <MenuItem
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  padding: 2,
                  borderBottom: index < notifications.length - 1 ? '1px solid #f0f0f0' : 'none',
                  backgroundColor: !notification.is_read ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                  '&:hover': {
                    backgroundColor: !notification.is_read ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <ListItemIcon>
                  <Avatar sx={{ 
                    width: 32, 
                    height: 32, 
                    bgcolor: getNotificationIconColor(notification.type),
                    fontSize: '1rem'
                  }}>
                    {getNotificationIcon(notification.type)}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" component="span">
                        {t(notification.title_key)}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5, whiteSpace: 'normal', wordWrap: 'break-word' }}>
                        {t(notification.message_key, notification.message_params)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {formatRelativeTime(notification.created_at)}
                      </Typography>
                    </Box>
                  }
                />
              </MenuItem>
            ))}
            
            {notifications.length > 0 && (
              <>
                <Divider />
                <Box sx={{ p: 1 }}>
                  <Button
                    fullWidth
                    size="small"
                    onClick={handleClearAllNotifications}
                    startIcon={<ClearAllIcon />}
                    sx={{ justifyContent: 'flex-start' }}
                    color="error"
                    disabled={loading}
                  >
                    {t('notifications.clearAll')}
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </Menu>
    </>
  );
};